import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { OrderRepositoryLocalStorage } from "Infrastructure/repositories/localStorage/OrderRepositoryLocalStorage";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType, IError } from "application/models/IError";
import { IActionModalHandler } from "components/actionModal/ActionModal";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { NfceService } from "domains/order/services/NfceService";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import {
  IPaymentRef,
  IPaymentResult,
} from "modules/payment/presentation/PaymentProvider";
import { useCallback, useImperativeHandle, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { GetError } from "utils/GetError";
import { EmitNfceUseCase } from "../application/useCases/EmitNfceUseCase";
import {
  ISyncOrderResult,
  SyncOrderUseCase,
} from "../application/useCases/SyncOrderUseCase";
import { IEmitedNfce } from "../domain/models/INfce";
import { IOrder } from "../domain/models/IOrder";
import { IOrderLog } from "../domain/models/IOrderLog";
import { IPedidoPosDTO, StatusPedido } from "../domain/models/IPedidoPosDTO";
import {
  IFinancialTransactionModel,
  Payment,
  TransactionType,
} from "../domain/models/Payment";
import { IOrderProviderRef } from "./OrderProvider";
import { ICustomerIdentificationHandler } from "./customerIdentification/CustomerIdentification";
import { useSession } from "application/contexts/session/SessionContext";
import { IPasswordPainel } from "Infrastructure/repositories/api/PasswordPainel";
import { GetPasswordUseCase } from "../application/useCases/GetPasswordUseCase";
import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import {
  PixApprovedTransaction,
  PixPendingTransaction,
} from "modules/payment/domain/models/PixApprovedTransaction";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import {
  PrinterStore,
  usePrinterStore,
} from "application/contexts/printer/PrinterStore";
import { useShallow } from "zustand/react/shallow";
import { CreatePrintOrderUseCase } from "../application/useCases/CreatePrintOrderUseCase";
import { CreatePrintNfceUseCase } from "../application/useCases/CreatePrintNfceUseCase";
import { CreatePrintRemoteUseCase } from "../application/useCases/CreatePrintRemoteUseCase";
import { CreatePrintReceiptUseCase } from "../application/useCases/CreatePrintReceiptUseCase";

const locaStorageOrderRepository = OrderRepositoryLocalStorage();
const nfceService = NfceService();

const UseOrderProvider = (
  orderProviderRef: React.ForwardedRef<IOrderProviderRef>,
  printReceiptRef: React.RefObject<IActionModalHandler>,
  printNfceRef: React.RefObject<IActionModalHandler>,
  customerRef: React.RefObject<ICustomerIdentificationHandler>
) => {
  const paymentProvider = useRef<IPaymentRef>(null);
  const countPaymentVoucher = useRef(0);
  const paymentReceipt = useRef<Payment>();
  const _order = useRef<IOrder>();

  const [addOrdersToPrintQueue] = usePrinterStore(
    useShallow((state: PrinterStore) => [state.addOrders])
  );

  const [clearCart] = useCartStore((state: ICartContext) => [state.clearCart]);
  const { sessaoIdPos } = useSession();
  const { toastFullscreen, toast, showLoading, hideLoading } = useUi();
  const { kiosk } = useKiosk();
  const { replace } = useHistory();
  const { cardData, isSchoolData } = useCardReader();
  const { addLog } = useAppInsights();
  const orderRepository = MeepOrderRepository(addLog);

  const [rechargePayment, setRechargePayment] = useState<
    "Recharge" | "Payment" | "WalletRecharge" | undefined
  >(undefined);

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          let orderLog = { ...order, payment, pedidoPOS, nfce };
          console.log({ orderLog, message });
          if (message.includes("Pagamento realizado")) {
            const log = {
              id: order.id,
              cartDate: order.cartDate,
              totalWithoutDiscount: order.totalWithoutDiscount,
              totalValue: order.totalValue,
              payment: order.payment,
              friendlyId: order.friendlyId,
              createdAt: order.createdAt,
              passwordPanelCode: order.passwordPanelCode,
              customer: order.customer,
              paymentPix: order.paymentPix,
              groupDiscountOrder: order.groupDiscountOrder,
            };
            addLog("order", { message, log });
          } else {
            addLog("order", { message, orderLog });
          }
          await locaStorageOrderRepository.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "OrderProvider",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const syncOrder = useCallback(
    async (order: IOrder, transactionType: TransactionType, kiosk: Kiosk) => {
      try {
        showLoading();
        const syncResult = await SyncOrderUseCase(
          orderRepository,
          order,
          transactionType,
          kiosk
        );
        await addOrderLog(order, "Pedido enviado", syncResult.pedidoPOS);
        orderRepository.syncOrdersAsync(kiosk.localId);
        return syncResult;
      } catch (error) {
        GetError(
          error,
          "syncOrder",
          "UseOrderProvider",
          "Falha ao sincronizar pedido",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addOrderLog, orderRepository, showLoading]
  );

  const printPaymentReceipt = useCallback(async () => {
    countPaymentVoucher.current = countPaymentVoucher.current + 1;
    try {
      if (paymentReceipt.current) {
        if (countPaymentVoucher.current <= 1) {
          addOrdersToPrintQueue(
            CreatePrintReceiptUseCase(paymentReceipt.current)
          )
          countPaymentVoucher.current = 0; // TODO: analisar regra
        }
      }
    } catch (error) {
      GetError(
        error,
        "printPaymentReceipt",
        "UseOrderProvider",
        "Falha ao imprimir comprovante",
        ErrorCodeType.GENERAL
      );
    }
  }, [addOrdersToPrintQueue]);

  const handleReceiptPrintModal = useCallback(
    async (kiosk: Kiosk, transactionType: TransactionType) => {
      if (kiosk.printReceipt) {
        if (transactionType !== TransactionType.pospaid) {
          try {
            hideLoading();
            await printReceiptRef.current?.open();
            printPaymentReceipt();
          } catch (error) {
            GetError(
              error,
              "printTicket",
              "UseOrderProvider",
              `${(error as any)?.message ?? "Tempo expirado"}`,
              ErrorCodeType.GENERAL
            );
          }
        }
      }
    },
    [hideLoading, printPaymentReceipt, printReceiptRef]
  );

  const emitNfce = useCallback(
    async (order: IOrder) => {
      try {
        addOrderLog(order, "Emitir NF");
        await orderRepository.syncOrdersAsync(kiosk?.localId ?? '');
        const nfce = await EmitNfceUseCase(nfceService, order);
        if (!!nfce.length) {
          await addOrderLog(order, "NF Emitida", undefined, undefined, nfce);
          return nfce;
        } else {
          await addOrderLog(order, "Falha ao emitir NF");
          GetError(
            new Error("Falha ao emitir NF"),
            "emitNfce",
            "OrderProvider",
            "Falha ao emitir NF",
            ErrorCodeType.NFCE
          );
        }
      } catch (error) {
        GetError(
          error,
          "emitNfce",
          "UseOrderProvider",
          "Falha ao emitir nota fiscal",
          ErrorCodeType.NFCE
        );
        await addOrderLog(
          order,
          `Falha ao emitir nota fiscal - ${
            (error as any)?.response?.data?.message &&
            (error as any)?.response?.data?.message
          }`
        );
      }
    },
    [addOrderLog, kiosk, orderRepository]
  );

  const printNfce = useCallback(
    async (nfce: IEmitedNfce[]) => {
      try {
        if (nfce) {
          addOrdersToPrintQueue(CreatePrintNfceUseCase(nfce));
        }
      } catch (error) {
        GetError(
          error,
          "printNfce",
          "UseOrderProvider",
          "Falha ao imprimir NF",
          ErrorCodeType.NFCE
        );
        toast("Falha ao imprimir NF", "error");
      }
    },
    [addOrdersToPrintQueue, toast]
  );

  const handleNfcePrintModal = useCallback(
    async (nfce: Promise<IEmitedNfce[] | undefined>, kiosk: Kiosk) => {
      try {
        if (kiosk.generateEletronicInvoice) {
          if (kiosk.mustPrintEletronicInvoice) {
            await printNfceRef.current?.open();
            showLoading();
            try {
              let nf: IEmitedNfce[] | undefined;
              try {
                nf = await nfce;
                if (!nf) {
                  toast("Falha ao emitir NF", "error");
                }
              } catch (error) {
                toast("Falha ao emitir NF", "error");
              }
              if (nf) {
                await printNfce(nf);
              }
            } catch (error) {
              GetError(
                error,
                "handleNfcePrintModal",
                "UseOrderProvider",
                `${(error as any)?.message ?? "Falha ao emitr NF"}`,
                ErrorCodeType.NFCE
              );
            } finally {
              hideLoading();
            }
          }
        }
      } catch (error) {
        GetError(
          error,
          "handleNfcePrintModal",
          "UseOrderProvider",
          `${(error as any)?.message ?? "Tempo expirado"}`,
          ErrorCodeType.NFCE
        );
      }
    },
    [
      hideLoading,
      printNfce,
      printNfceRef,
      showLoading,
      toast
    ]
  );

  const onSucessPayment = useCallback(
    async (
      orderWithCustomer: IOrder,
      transactionType: TransactionType,
      payment?: Payment,
      recharge?: "Recharge" | "Payment" | "WalletRecharge",
      pedidoPOS?: IPedidoPosDTO
    ) => {
      paymentReceipt.current = payment;
      _order.current = orderWithCustomer;
      if (kiosk) {
        const paidOrder = {
          ...orderWithCustomer,
          payment,
        };

        let password: IPasswordPainel | undefined;

        if (
          kiosk.mustEnableManagementPassword &&
          (!recharge || recharge === "Payment")
        ) {
          showLoading();
          password = await GetPasswordUseCase(
            orderRepository,
            kiosk.localId,
            sessaoIdPos ?? ""
          );
        }
        let syncResult: ISyncOrderResult | undefined;

        if (!pedidoPOS && (!recharge || recharge !== "WalletRecharge")) {
          syncResult = await syncOrder(
            { ...paidOrder, passwordPanelCode: password?.passwordForPrinting },
            transactionType,
            kiosk
          );
        } else {
          syncResult = {
            pedidoPOS,
          };
        }

        if (
          kiosk.mustEnableManagementPassword &&
          !password?.passwordForPrinting &&
          (!recharge || recharge === "Payment")
        ) {
          toastFullscreen(
            "Seu pedido foi gerado sem senha, Favor ir até o balcão",
            "warning"
          );
        }

        if (!recharge && syncResult?.pedidoPOS) {
          addLog("print", {
            message: "Start print",
            orderLog: { paidOrder, pedidoPos: syncResult.pedidoPOS },
          });
          addOrdersToPrintQueue(
            CreatePrintOrderUseCase(syncResult.pedidoPOS, kiosk)
          );
          addOrdersToPrintQueue(
            CreatePrintRemoteUseCase(
              paidOrder,
              syncResult.pedidoPOS,
              kiosk
            )
          );
        } else if (!recharge && !syncResult?.pedidoPOS) {
          addLog("print", {
            message: "Print no started: missing pedidoPOS",
            orderLog: { paidOrder },
          });
        }

        if (orderWithCustomer.totalValue > 0) {
          if (
            transactionType !== TransactionType.cashless &&
            transactionType !== TransactionType.pospaid &&
            transactionType !== TransactionType.pix //TODO: Remover ao criar comprovante pix
          ) {
            await handleReceiptPrintModal(kiosk, transactionType);
          }

          const isTransaction =
            transactionType === TransactionType.credit ||
            transactionType === TransactionType.debit ||
            transactionType === TransactionType.pix ||
            transactionType === TransactionType.voucher;

          const isRecharge = recharge === "Recharge";

          if (
            kiosk.generateEletronicInvoice &&
            ((!kiosk.generateEletronicInvoiceForRecharge && !isRecharge) ||
            (kiosk.generateEletronicInvoiceForRecharge && isTransaction))
          ) {
            if (recharge !== "WalletRecharge") {
              const nfce = emitNfce(paidOrder);
              handleNfcePrintModal(nfce, kiosk);
            }
          }
        }

        hideLoading();
      }
      clearCart();
    },
    [
      kiosk,
      clearCart,
      hideLoading,
      showLoading,
      orderRepository,
      sessaoIdPos,
      syncOrder,
      toastFullscreen,
      addLog,
      addOrdersToPrintQueue,
      handleReceiptPrintModal,
      emitNfce,
      handleNfcePrintModal,
    ]
  );

  const confirmPospaid = useCallback(
    async (
      orderWithCustomer: IOrder,
      transactionType: TransactionType,
      payment?: Payment,
      recharge?: "Recharge" | "Payment" | "WalletRecharge"
    ) => {
      paymentReceipt.current = payment;
      if (kiosk) {
        const paidOrder = {
          ...orderWithCustomer,
          payment,
        };

        let password: IPasswordPainel | undefined;

        if (
          kiosk.mustEnableManagementPassword &&
          (!recharge || recharge === "Payment")
        ) {
          showLoading();
          password = await GetPasswordUseCase(
            orderRepository,
            kiosk.localId,
            sessaoIdPos ?? ""
          );
        }

        const syncResult = await syncOrder(
          { ...paidOrder, passwordPanelCode: password?.passwordForPrinting },
          transactionType,
          kiosk
        );

        if (syncResult?.success) {
          await addOrderLog(
            { ...orderWithCustomer, paymentApproved: true },
            "Consumo aprovado",
            syncResult.pedidoPOS,
            {
              paymentMethod: transactionType,
            } as Payment
          );
          if (
            kiosk.mustEnableManagementPassword &&
            !password?.passwordForPrinting &&
            !recharge
          ) {
            toastFullscreen(
              "Seu pedido foi gerado sem senha, Favor ir até o balcão",
              "warning"
            );
          } else {
            !recharge &&
              toastFullscreen(
                kiosk?.messageOnPayment ?? "Pedido realizado com sucesso.",
                "success",
                {
                  onClose: () => {
                    replace("/");
                  },
                  time: 4000,
                }
              );
          }

          if (!recharge && syncResult?.pedidoPOS) {
            addOrdersToPrintQueue(
              CreatePrintOrderUseCase(syncResult.pedidoPOS, kiosk)
            );
            addOrdersToPrintQueue(
              CreatePrintRemoteUseCase(paidOrder, syncResult.pedidoPOS, kiosk)
            );
          }

          if (orderWithCustomer.totalValue > 0) {
            if (
              transactionType !== TransactionType.cashless &&
              transactionType !== TransactionType.pospaid
            ) {
              await handleReceiptPrintModal(kiosk, transactionType);
            }
          }

          clearCart();
          hideLoading();
          return true;
        } else {
          await addOrderLog(
            orderWithCustomer,
            syncResult?.message ?? "Consumo recusado",
            syncResult?.pedidoPOS
          );
          toast(syncResult?.message ?? "Falha ao sincronizar pedido", "error");
          hideLoading();
          return false;
        }
      }
    },
    [
      kiosk,
      syncOrder,
      showLoading,
      orderRepository,
      sessaoIdPos,
      addOrderLog,
      clearCart,
      hideLoading,
      toastFullscreen,
      replace,
      addOrdersToPrintQueue,
      handleReceiptPrintModal,
      toast,
    ]
  );

  const startPayment = useCallback(
    async (
      order: IOrder,
      recharge?: "Recharge" | "Payment" | "WalletRecharge",
      paymentType?: TransactionType
    ) => {
      setRechargePayment(recharge);
      try {
        if (order.orderItems.length === 0) {
          await addOrderLog(order, `Carrinho vazio`);
          throw GetError(
            new Error("Carrinho vazio"),
            "startPayment",
            "UseOrderProvider",
            "Carrinho vazio",
            ErrorCodeType.GENERAL
          );
        }

        if (
          (kiosk?.customerFieldEnable &&
            !kiosk.customerFieldDisabled &&
            kiosk.customerField.length) ||
          kiosk?.generateEletronicInvoice ||
          kiosk?.prism
        ) {
          const customer = await customerRef.current?.open(kiosk, recharge);
          if (customer) {
            order = {
              ...order,
              customer: {
                ...order.customer,
                name: customer.name ?? order.customer?.name ?? "",
                phone: customer.phone ?? order.customer?.phone ?? "",
                document: customer.document ?? order.customer?.document ?? "",
                documentToNf: customer.documentToNf ?? "",
                email: customer.email ?? order.customer?.email ?? "",
                clientIdentificator:
                  customer.clientIdentificator ??
                  order.customer?.clientIdentificator ??
                  "",
                prism: customer.prism ?? order.customer?.prism ?? "",
              },
            };
          }
        }

        await addOrderLog(order, "Pagamento Iniciado");
        let result: IPaymentResult | undefined = undefined;
        if (order.totalValue > 0) {
          const type =
            !kiosk?.paymentVoucher &&
            !kiosk?.paymentCredit &&
            !kiosk?.paymentDebit &&
            !kiosk?.paymentPix
              ? TransactionType.cashless
              : paymentType;
          result = await paymentProvider.current?.open(
            order,
            isSchoolData.current ? TransactionType.cashless : !recharge ? type : undefined,
            isSchoolData.current ? cardData ?? undefined : undefined
          );
        } else {
          result = {
            order,
            paymentType: TransactionType.money,
            payment: {
              success: true,
              amount: 0,
              paymentMethod: TransactionType.money,
              FinancialTransactionModel: {},
            },
          } as IPaymentResult;
        }
        if (!result) {
          await addOrderLog(order, `Falha no pagamento`);
          throw GetError(
            new Error("Falha ao efetuar pagamento"),
            "startPayment",
            "UseOrderProvider",
            "Falha ao efetuar pagamento",
            ErrorCodeType.GENERAL
          );
        } else {
          if (result.paymentType !== TransactionType.pospaid) {
            !recharge &&
              toastFullscreen(
                kiosk?.messageOnPayment ?? "Pedido realizado com sucesso.",
                "success",
                {
                  onClose: () => {
                    replace("/");
                  },
                  time: 4000,
                }
              );
            await addOrderLog(
              { ...result.order, paymentApproved: true },
              "Pagamento realizado",
              result.pedidoPos,
              result.payment
            );
            await onSucessPayment(
              result.order,
              result.paymentType,
              result.payment,
              recharge,
              result.pedidoPos
            );
            return result;
          } else {
            const posPaidResult = await confirmPospaid(
              result.order,
              result.paymentType,
              result.payment,
              recharge
            );
            result.payment.success = !!posPaidResult;
            return result;
          }
        }
      } catch (_error) {
        const isArray = (_error as IError[]).length > 0;
        const iError = isArray ? (_error as IError[])[0] : (_error as IError);
        if (iError?.message && iError.message.includes("Pix")) {
          try {
            const cancelledPix: PixPendingTransaction = JSON.parse(
              iError.message
            );
            const paymentPix: PixApprovedTransaction = {
              ownerId: cancelledPix?.ownerId ?? "",
              paymentId: cancelledPix?.paymentId ?? "",
              qrCodeLink: cancelledPix?.qrCodeLink ?? "",
              status: cancelledPix?.status ?? "pending",
              value: cancelledPix?.value ?? order.totalValue,
              acquirerType: cancelledPix.acquirerType,
              createdAt: cancelledPix.createdAt,
              endToEndId: cancelledPix.endToEndId,
            };
            await addOrderLog(
              {
                ...order,
                paymentPix,
              },
              cancelledPix?.message ?? `Falha no pagamento`,
              undefined,
              {
                FinancialTransactionModel: {
                  Status: StatusPedido.AguardandoPagamento,
                } as IFinancialTransactionModel,
              } as Payment
            );
          } catch (error) {
            await addOrderLog(
              order,
              (error as IError)?.message ?? `Falha no pagamento`
            );
            toast((error as IError)?.message ?? "Falha no pagamento", "error");
          } finally {
            throw GetError(
              _error,
              "startPayment",
              "UseOrderProvider",
              "Falha ao efetuar pagamento²",
              ErrorCodeType.PAYMENT
            );
          }
        } else {
          let errMsg = "";
          try {
            const parsedError = JSON.parse(iError.message);
            errMsg = parsedError?.message;
          } catch (err) {
            const message =
              typeof (_error as any)?.response?.data?.message === "string"
                ? (_error as any).response.data.message.split(" :").length > 0
                  ? (_error as any).response.data.message.split(" :")[1]
                  : (_error as any).response.data.message
                : iError.message;

            errMsg = message;
          }
          await addOrderLog(
            order, 
            `Falha no pagamento`, 
            undefined, 
            {
              errorMessage: JSON.stringify(_error)
            } as Payment
          );
          toast("[03] Falha no pagamento", "error");
          throw GetError(
            _error,
            "startPayment",
            "UseOrderProvider",
            "Falha ao efetuar pagamento³",
            ErrorCodeType.PAYMENT
          );
        }
      }
    },
    [
      addOrderLog,
      cardData,
      confirmPospaid,
      customerRef,
      isSchoolData,
      kiosk,
      onSucessPayment,
      replace,
      toast,
      toastFullscreen,
    ]
  );

  useImperativeHandle(orderProviderRef, () => ({ startPayment }));

  return {
    startPayment,
    paymentProvider,
    rechargePayment,
  };
};

export default UseOrderProvider;
